import { z } from "zod";

const emailSchema = z.string().email("Invalid email format").toLowerCase().trim();

/** Public nested subscription on GET /accounts/me (no stripe* ids / userId / persistence timestamps). */
export const currentUserSubscriptionSchema = z.object({
  status: z.string(),
  planId: z.string(),
  billingCycle: z.string(),
  currentPeriodStart: z.number(),
  currentPeriodEnd: z.number(),
  trialStart: z.number().optional(),
  trialEnd: z.number().optional(),
  canceledAt: z.number().optional(),
  cancelAtPeriodEnd: z.boolean(),
  lookupKey: z.string().optional(),
});

/** Public account profile returned by GET /accounts/me */
export const currentUserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string().nullable().optional(),
  username: z.string(),
  phoneNumber: z.string().nullable().optional(),
  role: z.string(),
  accountType: z.enum(["default", "google"]),
  emailVerified: z.boolean(),
  phoneVerified: z.boolean(),
  onboardingCompleted: z.boolean(),
  subscriptionId: z.string().nullable(),
  subscription: currentUserSubscriptionSchema.nullable().optional(),
  country: z.string().optional(),
  organizationName: z.string().nullable().optional(),
  avatarAssetId: z.string().nullable().optional(),
});

export type CurrentUserSubscription = z.infer<typeof currentUserSubscriptionSchema>;
export type CurrentUser = z.infer<typeof currentUserSchema>;


const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .max(100, "Password too long");

const usernamePattern = new RegExp("^[\\p{L}0-9_]+$", "u");

const usernameSchema = z
  .string()
  .min(3, "Username must be at least 3 characters")
  .max(30, "Username too long")
  .regex(usernamePattern, "Username can only contain letters, numbers, and underscores")
  .trim();

export const deleteAccountSchema = z.object({
  password: passwordSchema.optional(),
  repeatPassword: passwordSchema.optional(),
});

export const updateProfileNameSchema = z.object({
  name: z
    .string()
    .min(3, "Name must be at least 3 characters")
    .max(100, "Name too long")
    .trim()
    .refine((val) => val.split(" ").length >= 2, {
      message: "Please provide both first and last name",
    }),
});

export const updateProfileEmailSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
});

export const updateProfileUsernameSchema = z.object({
  username: usernameSchema,
});

export const verifyAccountEmailCodeSchema = z.object({
  code: z.string().min(6).max(16),
});

export type DeleteAccountRequest = z.infer<typeof deleteAccountSchema>;
export type UpdateProfileNameRequest = z.infer<typeof updateProfileNameSchema>;
export type UpdateProfileEmailRequest = z.infer<typeof updateProfileEmailSchema>;
export type UpdateProfileUsernameRequest = z.infer<typeof updateProfileUsernameSchema>;
export type VerifyAccountEmailCodeRequest = z.infer<typeof verifyAccountEmailCodeSchema>;
